import { useState } from "react";
import { toast } from "sonner";
import {
  Building2,
  FolderKanban,
  Plus,
  Trash2,
  Edit2,
  Check,
  X,
  Save,
  RotateCcw,
  Zap,
  ChevronRight,
  MessageCircle,
  Files,
  CalendarCheck,
  Calculator,
  ScrollText,
  Inbox,
  Settings as SettingsIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Lock } from "lucide-react";
import AppShell from "@/components/layout/AppShell";
import { cn } from "@/lib/utils";
import { useSettings } from "@/context/SettingsContext";
import { useAuth } from "@/context/AuthContext";
import { projectsApi } from "@/lib/crmApi";
import AutomationsPanel from "@/components/crm/AutomationsPanel";
import SequencesPanel from "@/components/crm/SequencesPanel";
import ProjectProfileDialog from "@/components/crm/ProjectProfileDialog";
import CloudAPIPanel from "@/components/crm/CloudAPIPanel";
import WhatsAppConnect from "@/components/crm/WhatsAppConnect";
import WelcomeAutoSend from "@/components/crm/WelcomeAutoSend";
import WelcomeSweepPanel from "@/components/crm/WelcomeSweepPanel";
import FollowupAutoSend from "@/components/crm/FollowupAutoSend";
import BookingSettingsPanel from "@/components/crm/BookingSettingsPanel";
import SimulationConfigPanel from "@/components/crm/SimulationConfigPanel";
import DocumentsLibraryPanel from "@/components/crm/DocumentsLibraryPanel";
import AuditLogPanel from "@/components/crm/AuditLogPanel";
import LeadSourcesPanel from "@/components/crm/LeadSourcesPanel";
// Le concept "Programme" a été fusionné dans "Projet" (mai 2026). Les
// catalogues, URLs site/vidéo et métadonnées propriété vivent désormais
// directement sur le projet. L'onglet dédié "Catalogue programmes" est
// retiré — voir l'onglet "Projets / Campagnes".

const SECTIONS = [
  { id: "company", label: "Entreprise", icon: Building2 },
  { id: "projects", label: "Projets / Campagnes", icon: FolderKanban },
  { id: "documents", label: "Documents", icon: Files },
  { id: "whatsapp", label: "WhatsApp", icon: MessageCircle },
  { id: "booking", label: "Prise de RDV", icon: CalendarCheck },
  { id: "simulation", label: "Simulation", icon: Calculator },
  { id: "automations", label: "Automatisations", icon: Zap },
  // v2.8.0 — Sources de leads externes (Facebook Lead Ads + Make.com).
  // Admin only car les secrets HMAC + tokens Graph y sont stockés.
  { id: "lead-sources", label: "Sources de leads", icon: Inbox, adminOnly: true },
  // v2.7.0 — Journal d'activité admin-only. Le tab reste affiché pour tous
  // les rôles mais le contenu côté backend renvoie 403 si non-admin (le
  // panel le détecte et affiche un placeholder). Ce choix garde la navigation
  // stable entre comptes (pas de menu qui change selon le rôle).
  { id: "audit", label: "Journal d'activité", icon: ScrollText, adminOnly: true },
];

function ProjectRow({ projet, isActive, onUpdate, onDelete, onSetActive }) {
  const { user } = useAuth();
  // v1.8.0 — destructive / config buttons are admin-only. Backend enforces
  // the 403 too; we hide the buttons so commercials don't see them, click,
  // and get a confusing error message.
  const isAdmin = user?.role === "admin";
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    nom: projet.nom,
    duree_jours: projet.duree_jours,
    description: projet.description || "",
  });
  // Password-gated delete: the destructive cascade (drops every lead +
  // rappel of the project) is now hidden behind a modal that asks the
  // admin to re-type their password. Native window.confirm was too easy
  // to click through.
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deletePassword, setDeletePassword] = useState("");
  const [deleting, setDeleting] = useState(false);
  // v1.8.0 — per-project profile dialog (AI prompt, welcome, templates…).
  // Lives next to the inline "Modifier" form which still handles
  // name / duration / description.
  const [profileOpen, setProfileOpen] = useState(false);

  const save = async () => {
    if (!form.nom.trim()) return toast.error("Nom requis");
    const d = Number(form.duree_jours);
    if (!d || d < 1 || d > 365) return toast.error("Durée entre 1 et 365");
    await projectsApi.update(projet.id, { ...form, duree_jours: d });
    toast.success("Projet mis à jour");
    setEditing(false);
    onUpdate();
  };

  const openDeleteDialog = () => {
    setDeletePassword("");
    setConfirmDelete(true);
  };

  const confirmRemove = async () => {
    if (!deletePassword) {
      toast.error("Saisis ton mot de passe pour confirmer");
      return;
    }
    setDeleting(true);
    try {
      const res = await projectsApi.remove(projet.id, { password: deletePassword });
      const ld = res?.leads_deleted ?? 0;
      const rd = res?.rappels_deleted ?? 0;
      toast.success(
        ld > 0
          ? `Projet supprimé · ${ld} lead(s) et ${rd} rappel(s) effacés`
          : "Projet supprimé"
      );
      setConfirmDelete(false);
      onDelete();
    } catch (e) {
      // Surface 401 (wrong password) differently from 409 (active project) so
      // the user knows what to fix.
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 401) {
        toast.error("Mot de passe incorrect");
      } else {
        toast.error(detail || "Erreur lors de la suppression");
      }
    } finally {
      setDeleting(false);
    }
  };

  const reset = async () => {
    const ok = window.confirm(
      `🔄 Réinitialiser "${projet.nom}" ?\n\nTous les leads et rappels de ce projet seront DÉFINITIVEMENT supprimés, mais le projet sera conservé.\n\nCette action est irréversible.`
    );
    if (!ok) return;
    try {
      const res = await projectsApi.reset(projet.id);
      const ld = res?.leads_deleted ?? 0;
      const rd = res?.rappels_deleted ?? 0;
      toast.success(
        ld > 0 ? `Projet réinitialisé · ${ld} lead(s) et ${rd} rappel(s) effacés` : "Projet déjà vide"
      );
      onUpdate();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de la réinitialisation");
    }
  };

  if (editing) {
    return (
      <div
        className="rounded-xl border border-border/60 bg-muted/30 p-4"
        data-testid={`project-edit-${projet.id}`}
      >
        <div className="grid gap-3 md:grid-cols-[1fr_140px]">
          <div className="space-y-1.5">
            <Label className="text-[11px]">Nom du projet</Label>
            <Input
              value={form.nom}
              onChange={(e) => setForm({ ...form, nom: e.target.value })}
              placeholder="Nom du projet"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-[11px]">Durée (jours)</Label>
            <Input
              type="number"
              min={1}
              max={365}
              value={form.duree_jours}
              onChange={(e) => setForm({ ...form, duree_jours: e.target.value })}
            />
          </div>
        </div>
        <div className="mt-3 space-y-1.5">
          <Label className="text-[11px]">Description (optionnel)</Label>
          <Textarea
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            rows={2}
          />
        </div>
        <div className="mt-3 flex gap-2">
          <Button size="sm" onClick={save} className="gap-1.5">
            <Save className="h-3.5 w-3.5" />
            Enregistrer
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setEditing(false)} className="gap-1.5">
            <X className="h-3.5 w-3.5" />
            Annuler
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "rounded-xl border p-4 transition-all card-hover",
        isActive
          ? "border-accent-gold bg-amber-50/40 shadow-soft dark:bg-amber-950/20"
          : "border-border/60 bg-card"
      )}
      data-testid={`project-row-${projet.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <FolderKanban className="h-4 w-4 flex-none text-accent-gold" />
            <span className="font-heading font-semibold">{projet.nom}</span>
            <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium">
              {projet.duree_jours} jours
            </span>
            {isActive && (
              <span className="rounded-full bg-accent-navy px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
                Actif
              </span>
            )}
          </div>
          {projet.description && (
            <p className="mt-1.5 text-xs text-muted-foreground">{projet.description}</p>
          )}
        </div>
        <div className="flex items-center gap-1">
          {!isActive && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => onSetActive(projet.id)}
              data-testid={`project-set-active-${projet.id}`}
              className="h-7 gap-1 text-xs"
            >
              <Check className="h-3 w-3" />
              <span className="hidden sm:inline">Définir actif</span>
            </Button>
          )}
          {/* Admin-only edit/profile actions. Commercials see only "Définir
              actif" (their main interaction with a project row in this
              page). */}
          {isAdmin && (
            <>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setEditing(true)}
                data-testid={`project-edit-btn-${projet.id}`}
                className="h-7 gap-1 px-2 text-xs"
                title="Modifier le nom, la durée ou la description du projet"
              >
                <Edit2 className="h-3 w-3" />
                <span className="hidden sm:inline">Modifier</span>
              </Button>
              {/* v1.8.0 — Profil dédié du projet (AI, welcome, templates,
                  logo, kill-switch automatisations). Bouton distinct de
                  "Modifier" qui reste cantonné aux 3 champs simples. */}
              <Button
                size="sm"
                variant="outline"
                onClick={() => setProfileOpen(true)}
                data-testid={`project-profile-btn-${projet.id}`}
                className="h-7 gap-1 px-2 text-xs"
                title="Configurer le profil du projet (AI, welcome, templates, branding)"
              >
                <SettingsIcon className="h-3 w-3" />
                <span className="hidden sm:inline">Profil</span>
              </Button>
            </>
          )}
          {/* Destructive actions — admin only. */}
          {isAdmin && (
            <>
              <Button
                size="sm"
                variant="ghost"
                onClick={reset}
                data-testid={`project-reset-${projet.id}`}
                className="h-7 gap-1 px-2 text-xs text-muted-foreground hover:text-amber-600"
                title="Supprimer tous les leads + rappels de ce projet (le projet lui-même est conservé)"
              >
                <RotateCcw className="h-3 w-3" />
                <span className="hidden sm:inline">Vider</span>
              </Button>
              {!isActive && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={openDeleteDialog}
                  data-testid={`project-delete-${projet.id}`}
                  className="h-7 w-7 p-0 text-muted-foreground hover:text-red-500"
                  title="Supprimer"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Password-gated delete confirmation. The admin re-enters their own
          password — a stolen cookie / shared workstation isn't enough to
          wipe a project + all its leads + all its rappels. */}
      <Dialog open={confirmDelete} onOpenChange={(o) => !deleting && setConfirmDelete(o)}>
        <DialogContent className="sm:max-w-md" data-testid={`project-delete-dialog-${projet.id}`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="h-4 w-4 text-red-600" />
              Supprimer « {projet.nom} »
            </DialogTitle>
            <DialogDescription>
              Action <strong>irréversible</strong> : tous les leads de ce projet
              et leurs rappels seront effacés en cascade. Saisis ton mot de
              passe administrateur pour confirmer.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800 dark:border-red-900/40 dark:bg-red-950/30 dark:text-red-300">
              ⚠️ Cette opération supprime le projet, tous ses leads et tous
              leurs rappels. Aucune restauration possible côté CRM (seul un
              backup manuel pourrait revenir en arrière).
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Mot de passe administrateur</Label>
              <Input
                type="password"
                value={deletePassword}
                onChange={(e) => setDeletePassword(e.target.value)}
                placeholder="Ton mot de passe pour confirmer"
                autoComplete="current-password"
                data-testid={`project-delete-password-${projet.id}`}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && deletePassword) confirmRemove();
                }}
                autoFocus
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setConfirmDelete(false)}
              disabled={deleting}
            >
              Annuler
            </Button>
            <Button
              onClick={confirmRemove}
              disabled={deleting || !deletePassword}
              className="bg-red-600 text-white hover:bg-red-700"
              data-testid={`project-delete-confirm-${projet.id}`}
            >
              {deleting ? "Suppression…" : "Confirmer la suppression"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* v1.8.0 — per-project profile editor (admin-only, server enforces).
          Mounted unconditionally so React keeps its internal state across
          open/close; `open` controls visibility. */}
      <ProjectProfileDialog
        open={profileOpen}
        onOpenChange={setProfileOpen}
        projet={projet}
        onSaved={onUpdate}
      />
    </div>
  );
}

function NewProjectForm({ onCreated }) {
  const [nom, setNom] = useState("");
  const [duree, setDuree] = useState(20);
  const [desc, setDesc] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!nom.trim()) return toast.error("Nom requis");
    const d = Number(duree);
    if (!d || d < 1 || d > 365) return toast.error("Durée entre 1 et 365 jours");
    setSaving(true);
    try {
      await projectsApi.create({ nom: nom.trim(), duree_jours: d, description: desc });
      toast.success("Projet créé");
      setNom("");
      setDuree(20);
      setDesc("");
      onCreated();
    } catch (e) {
      toast.error("Erreur");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="rounded-xl border-2 border-dashed border-border/70 bg-muted/20 p-4"
      data-testid="new-project-form"
    >
      <div className="text-label mb-3 text-muted-foreground">Ajouter un projet</div>
      <div className="grid gap-3 md:grid-cols-[1fr_140px]">
        <div className="space-y-1.5">
          <Label className="text-[11px]">Nom</Label>
          <Input
            value={nom}
            onChange={(e) => setNom(e.target.value)}
            placeholder="Ex : RYAD Mermoz 2026"
            data-testid="new-project-nom"
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-[11px]">Durée (jours)</Label>
          <Input
            type="number"
            min={1}
            max={365}
            value={duree}
            onChange={(e) => setDuree(e.target.value)}
            data-testid="new-project-duree"
          />
        </div>
      </div>
      <div className="mt-3 space-y-1.5">
        <Label className="text-[11px]">Description (optionnel)</Label>
        <Textarea
          value={desc}
          onChange={(e) => setDesc(e.target.value)}
          rows={2}
        />
      </div>
      <Button
        onClick={submit}
        disabled={saving}
        data-testid="new-project-submit"
        className="mt-3 gap-2"
      >
        <Plus className="h-4 w-4" />
        {saving ? "Création..." : "Créer le projet"}
      </Button>
    </div>
  );
}

// Sections whose configuration is specific to each project. When no project
// is active (admin on "Tous les projets"), these require picking one first.
// NOTE: "whatsapp" is intentionally NOT gated. With a project active the
// section manages that project's dedicated session; with no project ("Tous
// les projets") it manages the DEFAULT session — the number that sends the
// automatic welcome messages. Gating it would make the default session
// (and its QR re-pairing) unreachable when a project is forced in the topbar.
const PER_PROJECT_SECTIONS = new Set([
  "documents",
  "booking",
  "simulation",
  "automations",
  "lead-sources",
]);

function ProjectGate() {
  return (
    <section
      className="rounded-2xl border border-dashed border-border/60 bg-card p-8 text-center animate-fade-in"
      data-testid="settings-project-gate"
    >
      <FolderKanban className="mx-auto h-8 w-8 text-muted-foreground/40" />
      <h2 className="mt-3 font-heading text-lg font-bold">Sélectionnez un projet</h2>
      <p className="mt-1 text-sm text-muted-foreground">
        Ce paramètre est spécifique à chaque projet. Choisissez un projet dans
        le sélecteur en haut de la page pour le configurer.
      </p>
    </section>
  );
}

export default function SettingsPage() {
  const settings = useSettings() || {};
  const { user } = useAuth();
  // v2.0.0 — the Cloud API config panel is admin-only; the surrounding
  // WhatsApp section is otherwise visible to everyone.
  const isAdmin = user?.role === "admin";
  const {
    companyName,
    activeProjectId,
    setCompanyName,
    setActiveProject,
    refresh,
  } = settings;
  const projects = Array.isArray(settings.projects) ? settings.projects : [];
  const [name, setName] = useState(companyName);
  const [saving, setSaving] = useState(false);
  const [section, setSection] = useState("company");
  // v1.8.1 — the WhatsApp and Simulation sections now always edit the
  // ACTIVE project. The old per-section scope selector confused users
  // (two ways to pick a project: topbar AND in-section) and made it easy
  // to leak config across projects. The topbar `ProjectSelector` is now
  // the single source of truth. When the active project is null (admin
  // viewing "Tous les projets"), we fall back to editing the global
  // defaults — same legacy behaviour, just without the in-section UI.
  const waScope = settings.activeProjectId || null;
  const waScopeNom = settings.activeProject?.nom || "";
  const simScope = settings.activeProjectId || null;
  const simScopeNom = settings.activeProject?.nom || "";
  // Project scope shared by the per-project settings panels.
  const projScope = settings.activeProjectId || null;
  const projScopeNom = settings.activeProject?.nom || "";
  // Block per-project sections until a project is selected (user choice:
  // force selection rather than editing the global defaults).
  const blocked = PER_PROJECT_SECTIONS.has(section) && !projScope;

  const saveCompany = async () => {
    if (!name.trim()) return toast.error("Nom requis");
    setSaving(true);
    try {
      await setCompanyName(name.trim());
      toast.success("Nom de l'entreprise mis à jour");
    } catch (e) {
      toast.error("Erreur");
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppShell topbarProps={{ showSearch: false, showAdd: false }}>
      <div className="mx-auto max-w-[1200px] px-4 py-5 sm:px-6 sm:py-6 lg:px-8 lg:py-8">
        {/* Page header */}
        <div className="mb-6 flex flex-col gap-1">
          <p className="text-label text-muted-foreground">Configuration</p>
          <h1 className="font-heading text-2xl font-bold tracking-tight sm:text-3xl">
            Paramètres
          </h1>
          <p className="text-sm text-muted-foreground">
            Gérez votre entreprise, vos projets et vos automatisations.
          </p>
        </div>

        <div className="grid gap-6 lg:grid-cols-[240px_1fr]">
          {/* Section nav */}
          <nav className="lg:sticky lg:top-20 lg:self-start">
            {/* Mobile pills */}
            <div className="scrollbar-thin -mx-4 mb-4 flex gap-2 overflow-x-auto px-4 lg:hidden">
              {SECTIONS.filter((s) => !s.adminOnly || isAdmin).map((s) => {
                const Icon = s.icon;
                const active = section === s.id;
                return (
                  <button
                    key={s.id}
                    onClick={() => setSection(s.id)}
                    data-testid={`settings-tab-${s.id}`}
                    className={cn(
                      "flex flex-none items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors",
                      active
                        ? "border-accent-navy bg-accent-navy text-white dark:bg-primary dark:text-primary-foreground"
                        : "border-border bg-card hover:bg-muted"
                    )}
                  >
                    <Icon className="h-3.5 w-3.5" />
                    {s.label}
                  </button>
                );
              })}
            </div>
            {/* Desktop list */}
            <div className="hidden space-y-1 lg:block">
              {SECTIONS.filter((s) => !s.adminOnly || isAdmin).map((s) => {
                const Icon = s.icon;
                const active = section === s.id;
                return (
                  <button
                    key={s.id}
                    onClick={() => setSection(s.id)}
                    data-testid={`settings-nav-${s.id}`}
                    className={cn(
                      "group flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors",
                      active
                        ? "bg-accent-navy text-white shadow-soft dark:bg-primary dark:text-primary-foreground"
                        : "hover:bg-muted"
                    )}
                  >
                    <Icon className="h-4 w-4" strokeWidth={active ? 2.5 : 2} />
                    <span className="flex-1 text-left">{s.label}</span>
                    {active && <ChevronRight className="h-3.5 w-3.5" />}
                  </button>
                );
              })}
            </div>
          </nav>

          {/* Content */}
          <div className="min-w-0 space-y-6">
            {blocked ? (
              <ProjectGate />
            ) : (
            <>
            {section === "company" && (
              <section
                className="rounded-2xl border border-border/60 bg-card p-5 shadow-soft sm:p-6 animate-fade-in"
                data-testid="section-company"
              >
                <div className="mb-5 flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-accent-navy/10 text-accent-navy dark:bg-accent-navy/30 dark:text-blue-300">
                    <Building2 className="h-5 w-5" />
                  </div>
                  <div>
                    <h2 className="font-heading text-lg font-bold">Entreprise</h2>
                    <p className="text-xs text-muted-foreground">
                      Nom affiché dans le header et les exports.
                    </p>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="company-name-input">Nom de l'entreprise</Label>
                  <div className="flex flex-col gap-2 sm:flex-row">
                    <Input
                      id="company-name-input"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="SOGI SARL"
                      data-testid="company-name-input"
                      className="flex-1"
                    />
                    <Button
                      onClick={saveCompany}
                      disabled={saving || name === companyName}
                      data-testid="company-name-save"
                      className="gap-2"
                    >
                      <Save className="h-4 w-4" />
                      Enregistrer
                    </Button>
                  </div>
                </div>
              </section>
            )}

            {section === "projects" && (
              <section
                className="rounded-2xl border border-border/60 bg-card p-5 shadow-soft sm:p-6 animate-fade-in"
                data-testid="section-projects"
              >
                <div className="mb-5 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-50 text-accent-gold dark:bg-amber-950/40">
                      <FolderKanban className="h-5 w-5" />
                    </div>
                    <div>
                      <h2 className="font-heading text-lg font-bold">Projets immobiliers</h2>
                      <p className="text-xs text-muted-foreground">
                        {projects.length} projet{projects.length > 1 ? "s" : ""} configurés
                      </p>
                    </div>
                  </div>
                </div>

                <p className="mb-4 rounded-lg bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
                  Chaque projet définit sa propre durée de programme de conversion. Le projet actif s'applique par défaut aux nouveaux leads.
                </p>

                <Separator className="mb-4" />

                <div className="space-y-3" data-testid="projects-list">
                  {projects.length === 0 ? (
                    <p className="rounded-lg border border-dashed border-border/60 p-6 text-center text-sm text-muted-foreground">
                      Aucun projet. Créez-en un ci-dessous.
                    </p>
                  ) : (
                    projects.map((p) => (
                      <ProjectRow
                        key={p.id}
                        projet={p}
                        isActive={activeProjectId === p.id}
                        onUpdate={refresh}
                        onDelete={refresh}
                        onSetActive={async (id) => {
                          await setActiveProject(id);
                          toast.success(`"${p.nom}" défini comme projet actif`);
                        }}
                      />
                    ))
                  )}
                </div>

                <div className="mt-4">
                  <NewProjectForm onCreated={refresh} />
                </div>
              </section>
            )}

            {section === "documents" && (
              <DocumentsLibraryPanel
                key={`docs-${projScope || "__global__"}`}
                projetId={projScope}
                projetNom={projScopeNom}
              />
            )}

            {section === "whatsapp" && (
              <div className="animate-fade-in" data-testid="section-whatsapp">
                <div className="mb-5 flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300">
                    <MessageCircle className="h-5 w-5" />
                  </div>
                  <div>
                    <h2 className="font-heading text-lg font-bold">
                      Connexion WhatsApp
                      {waScopeNom && (
                        <span className="ml-2 text-sm font-normal text-muted-foreground">
                          · {waScopeNom}
                        </span>
                      )}
                    </h2>
                    <p className="text-xs text-muted-foreground">
                      {waScope
                        ? `Numéro WhatsApp Business dédié au projet ${waScopeNom}. Pour basculer sur un autre projet, utilisez le sélecteur en haut.`
                        : "Numéro WhatsApp Business par défaut (utilisé quand un projet n'a pas son propre numéro)."}
                    </p>
                  </div>
                </div>
                {/* v1.8.1 — le scope vient de la topbar (activeProjectId).
                    On affiche UNIQUEMENT le profil WhatsApp du projet
                    actif : pas de mélange "Global vs project". L'admin
                    bascule entre projets via le sélecteur en haut. */}
                <WhatsAppConnect
                  key={`wa-connect-${waScope || "__default__"}`}
                  sessionId={waScope || null}
                  projetNom={waScopeNom}
                />
                {/* v2.0.0 — Cloud API (Meta) panel. Admin-only. Visible
                    seulement quand un projet est actif (le scope ne peut
                    pas être global car la conf Meta est par-projet). */}
                {waScope && settings.activeProject && isAdmin && (
                  <CloudAPIPanel
                    key={`cloud-${waScope}`}
                    projet={settings.activeProject}
                    onSaved={() => settings.refresh?.()}
                  />
                )}
                <WelcomeAutoSend
                  key={waScope || "__global__"}
                  projetId={waScope}
                  projetNom={waScopeNom}
                />
                <FollowupAutoSend />
                {/* Vérification welcome — observabilité du sweep automatique
                    qui tourne toutes les 30 min. Pour être SÛR que tout le
                    monde reçoit son message de bienvenue. */}
                <WelcomeSweepPanel />
              </div>
            )}

            {section === "booking" && (
              <BookingSettingsPanel
                key={`booking-${projScope || "__global__"}`}
                projetId={projScope}
                projetNom={projScopeNom}
              />
            )}

            {section === "simulation" && (
              <div className="animate-fade-in">
                {/* v1.8.1 — scope = projet actif (topbar). Pas de sélecteur
                    "Global vs Projet" inline qui doublonnait avec le
                    sélecteur en haut. Pour configurer la simulation
                    globale, l'admin choisit "Tous les projets" en haut. */}
                {simScopeNom && (
                  <div className="mb-4 rounded-lg border border-border/60 bg-muted/20 px-3 py-2 text-xs">
                    Configuration de la simulation pour{" "}
                    <span className="font-semibold">{simScopeNom}</span>.
                    Bascule de projet via la topbar.
                  </div>
                )}
                <SimulationConfigPanel
                  key={`sim-${simScope || "__global__"}`}
                  projetId={simScope}
                  projetNom={simScopeNom}
                />
              </div>
            )}

            {section === "automations" && (
              <div className="animate-fade-in">
                <AutomationsPanel
                  key={`auto-${projScope || "__global__"}`}
                  projetId={projScope}
                  projetNom={projScopeNom}
                />
                {/* v2.3.0 — Séquences automatiques, admin uniquement.
                    Scope = projet actif (topbar). Si "Tous les projets"
                    en admin, on affiche les séquences globales. */}
                {isAdmin && (
                  <SequencesPanel projet={settings.activeProject} />
                )}
              </div>
            )}

            {section === "lead-sources" && isAdmin && (
              <div className="animate-fade-in">
                <LeadSourcesPanel
                  key={`leadsrc-${projScope || "__global__"}`}
                  projetId={projScope}
                  projetNom={projScopeNom}
                />
              </div>
            )}

            {section === "audit" && isAdmin && (
              <div className="animate-fade-in">
                <AuditLogPanel />
              </div>
            )}
            </>
            )}
          </div>
        </div>
      </div>
    </AppShell>
  );
}
